import { useState } from 'react'

import i18n from '@signavio/i18n'
import { DangerHint, List, PlaceholderTile, useStyles } from '@signavio/ui'
import {
  DataSource,
  DataSourceT,
  ThemeT,
  useLazyAutocompleteQuery,
} from '@signavio/effektif-commons'

import { useApi } from '../../../api'
import apiTypes from '../../../apiTypes'
import { useCallbackWhenRequestFulfills } from '../../../hooks'
import User from '../User'
import EmptyIndicator from './EmptyIndicator'
import { UserType } from '../../../../types'

type UserDataSourceProps = {
  children?: (user: DataSourceT) => React.ReactNode
  disableUser?: (user: UserType) => boolean
  disabledUserIds?: string[]
  disabledGroupIds?: string[]
  groupIds?: string[]
  onDataReceived?: (users: UserType[]) => void
  onSelect?: (user: UserType) => void
  showHintWhenEmpty?: boolean
  userIds?: string[]
}

const pagesize = 30

function UserDataSource({
  children,
  disableUser,
  disabledUserIds = [],
  disabledGroupIds = [],
  groupIds,
  onDataReceived,
  onSelect,
  showHintWhenEmpty,
  userIds,
}: UserDataSourceProps) {
  const styles = useStyles(defaultStyles, {})
  const [resultSize, setResultSize] = useState(0)

  const name = useLazyAutocompleteQuery()

  const [users] = useApi(apiTypes.USERS, {
    query: { name, groupIds, userIds },
    requestParams: { offset: 0, pagesize },
  })

  useCallbackWhenRequestFulfills(users, (users, { responseHeaders }) => {
    setResultSize(
      Math.ceil(parseInt(responseHeaders.get('meta-result-size') as string))
    )

    if (onDataReceived) {
      onDataReceived(users as UserType[])
    }
  })

  if (users.pending) {
    return (
      <List>
        {Array(pagesize)
          .fill(null)
          .map((_, index) => (
            /* index is the only option available */
            /* eslint-disable react/no-array-index-key*/
            <PlaceholderTile key={index} transparent withHeader withSubtitle />
          ))}
      </List>
    )
  }

  if (users.rejected) {
    return (
      <DangerHint>
        {i18n('Could not load users for the following reason: __reason__', {
          reason: users.reason,
        })}
      </DangerHint>
    )
  }

  if (!users.value) {
    return null
  }

  const data = users.value

  return (
    <>
      <DataSource
        type="user"
        data={data}
        emptyHint={
          showHintWhenEmpty ? (
            <EmptyIndicator hintText={i18n('No users found.')} />
          ) : undefined
        }
        disableItem={(user) => {
          if (disableUser) {
            return disableUser(user as UserType)
          }

          return (
            disabledUserIds.includes(user.id) ||
            (user as UserType).groupIds.some((groupId) =>
              disabledGroupIds.includes(groupId)
            )
          )
        }}
        onSelect={(user) => {
          if (onSelect) {
            onSelect(user as UserType)
          }
        }}
      >
        {children || ((user) => <User circular transparent value={user.id} />)}
      </DataSource>

      {resultSize > data.length && (
        <div {...styles('pagination')}>
          {i18n('Showing __current__ of __total__ users', {
            current: data.length,
            total: resultSize,
          })}
        </div>
      )}
    </>
  )
}

const defaultStyles = ({ spacing, color, font }: ThemeT) => ({
  pagination: {
    marginTop: spacing.xxsmall,
    marginBottom: spacing.xxsmall,

    color: color.mono.middle,
    fontSize: font.size.small,

    display: 'flex',
    justifyContent: 'center',
  },
})

export default UserDataSource
